import { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import queryString from 'query-string';
import {
  useLocation,
  useNavigate,
} from 'react-router';
import {
  Authentication,
  store,
} from 'vibeguide';
import { markPageLoading } from '../../actions/loading-actions';
import {
  getExchangeErrorMessage,
  getMissingClientIdMessage,
  SSO_ERROR_STORAGE_KEY,
} from './ssoErrors';

function SsoCallbackHandler({
  authenticated,
  loading,
  markPageLoading,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [ssoCallbackHandled, setSsoCallbackHandled] = useState(false);

  const clearSsoCallbackParams = useCallback(() => {
    const params = queryString.parse(location.search);

    if (params.code || params.state) {
      navigate({
        pathname: location.pathname,
        search: '',
      }, { replace: true });
    }
  }, [navigate, location.pathname, location.search]);

  const goToLoginWithError = useCallback((message) => {
    sessionStorage.setItem(SSO_ERROR_STORAGE_KEY, message);
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (authenticated || loading || ssoCallbackHandled) {
      return;
    }

    const { code } = queryString.parse(location.search);

    if (!code) {
      return;
    }

    const clientId = Authentication.getSsoClientId();

    if (!clientId) {
      goToLoginWithError(getMissingClientIdMessage());
      clearSsoCallbackParams();
      setSsoCallbackHandled(true);
      return;
    }

    setSsoCallbackHandled(true);

    const completeSsoExchange = async () => {
      try {
        markPageLoading(true);
        await Authentication.exchangeSsoCode({ code, clientId });
        Authentication.clearSsoClientId();

        // Federated SSO users have no Cognito-managed password, so the
        // exchange can only resolve to authenticated or error — never a
        // challenge. Drive the redirect imperatively from the freshly-
        // authenticated user so we replace the `?code=...` URL in a single
        // navigate call (avoids a race where multiple back-to-back navigates
        // can leave the user stuck at `/`).
        const authedUser = store.getState().login.user;

        if (authedUser?._id) {
          navigate(authedUser.getRootUrl(), { replace: true });
        } else {
          clearSsoCallbackParams();
        }
      } catch (err) {
        Authentication.clearSsoClientId();
        clearSsoCallbackParams();
        goToLoginWithError(getExchangeErrorMessage(err));
      }

      markPageLoading(false);
    };

    void completeSsoExchange();
  }, [
    authenticated,
    loading,
    location.search,
    ssoCallbackHandled,
    clearSsoCallbackParams,
    markPageLoading,
    goToLoginWithError,
    location.pathname,
    navigate,
  ]);

  return null;
}

function mapStateToProps(state) {
  return {
    authenticated: state.login.authenticated,
    loading: state.loading.pageLoading,
  };
}

export default connect(mapStateToProps, { markPageLoading })(SsoCallbackHandler);
